import { useEffect, useState } from "preact/hooks";
import { MAX_PARTS } from "../outbox/retry-unit";
import { captureAttr } from "../shell/capture-affordance";
import { BackLink } from "./BackLink";
import { PillButton } from "./PillButton";
import { makeThumbUrl, revokeThumbUrl } from "./thumb-url";

/** §7.M3 Text modality — a full peer of voice-record (Red 2026-07-23): loud
 * site / PM at lunch → type the note instead of recording it. The typed note
 * is the capture's content (rides the commit as `payload.transcript`, the same
 * field an STT transcript would take — text IS the note), and photos can be
 * added on THIS same capture screen (screen-2-for-text) exactly like the voice
 * ActiveCapture path, before landing on Save/tag. Photos are real
 * `primary_photo` parts; the note carries no media part (MediaRole, locked, has
 * no text role). */
export function CaptureTextEntry({
  onAddPhoto,
  onSave,
  onCancel,
  partCount,
}: {
  onAddPhoto: (file: File) => void;
  onSave: (note: string) => void;
  onCancel: () => void;
  partCount: number;
}) {
  const [note, setNote] = useState("");
  const canSave = note.trim().length > 0;
  const atBound = partCount >= MAX_PARTS;

  // Photo thumbnails, same as the voice ActiveCapture path. Text capture used to
  // show only the "n / 20" count, so adding a photo gave a bare number back with
  // no confirmation the right shot was taken — text is a peer of record, and it
  // should confirm a capture the same way. Blobs here are PRE-commit (in memory,
  // not yet in IndexedDB), so these are built from the file directly; the
  // IDB-reading CaptureThumbs would render nothing at this point.
  const [thumbs, setThumbs] = useState<string[]>([]);
  useEffect(() => {
    // Revoke on unmount only — `thumbs` is intentionally not a dep, since
    // re-running on every append would revoke URLs still being displayed.
    return () => thumbs.forEach((u) => revokeThumbUrl(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <section data-skin-slot="SK-CAPTURE" data-testid="text-capture" style={{ display: "flex", flexDirection: "column", gap: "16px", flex: 1, textAlign: "left", alignItems: "stretch" }}>
      <BackLink label="Record" testid="text-capture-back" onBack={onCancel} />
      <p style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "var(--mark-text-primary)" }}>Type it instead</p>
      <textarea
        data-testid="text-capture-input"
        value={note}
        onInput={(e) => setNote((e.currentTarget as HTMLTextAreaElement).value)}
        placeholder="e.g. scratch on the walnut sill in the master bath, needs the wax guy"
        style={{ minHeight: "160px", resize: "vertical" }}
      />
      {/* Add photos on the same screen — text is a peer of record, not a lesser
          path. Same 20-part bound + static microcopy as ActiveCapture. */}
      <label class="capture-photo-btn" data-testid="text-capture-add-photo-label" data-disabled={atBound}>
        <input
          type="file"
          accept="image/*"
          capture={captureAttr()}
          data-testid="text-capture-add-photo"
          disabled={atBound}
          onChange={(e) => {
            const file = (e.currentTarget as HTMLInputElement).files?.[0];
            if (file) {
              onAddPhoto(file);
              const url = makeThumbUrl(file);
              if (url) setThumbs((prev) => [...prev, url]);
            }
            (e.currentTarget as HTMLInputElement).value = "";
          }}
        />
        {atBound ? "Photo limit reached (20 parts max)" : "Add photo"}
      </label>

      {thumbs.length > 0 && (
        <div class="capture-thumbs" data-testid="text-capture-thumbs">
          {thumbs.map((src, i) => (
            <span key={i} class="capture-thumb" data-testid={`text-capture-thumb-${i}`} style={{ backgroundImage: `url(${src})` }} />
          ))}
          <span class="capture-thumb-badge">{thumbs.length}</span>
        </div>
      )}
      <p data-testid="text-capture-part-count" style={{ color: "var(--mark-text-muted)", margin: 0 }}>
        {partCount} / {MAX_PARTS}
      </p>
      <PillButton fullWidth data-testid="text-capture-continue" disabled={!canSave} onClick={() => onSave(note.trim())}>
        Continue
      </PillButton>
    </section>
  );
}
